/**
 * ADMIN management of the NG word list (requirement 4.9).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../lib/api-client';
import type { NgWord, NgWordAction } from '../../lib/types';
import { NG_ACTION_LABELS } from './moderationLabels';

const ACTION_HELP: Record<NgWordAction, string> = {
  block: 'どこで使われても保存・送信を拒否します',
  review: '視聴者の投稿は受け付けて検知を記録し、配信者のプロフィールとお知らせでは拒否します',
};

export default function NgWordManagementPanel() {
  const [words, setWords] = useState<NgWord[]>([]);
  const [filter, setFilter] = useState('');
  const [newWord, setNewWord] = useState('');
  const [newAction, setNewAction] = useState<NgWordAction>('block');
  const [confirmDelete, setConfirmDelete] = useState<NgWord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setWords(await apiClient.listNgWords());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'NGワードを取得できませんでした');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(
    () => words.filter((word) => word.word.toLowerCase().includes(filter.trim().toLowerCase())),
    [words, filter],
  );

  const run = async (operation: () => Promise<void>, message: string) => {
    try {
      await operation();
      setNotice(message);
      setError(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '操作に失敗しました');
    }
  };

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    const word = newWord.trim();
    if (!word) return;
    await run(async () => { await apiClient.createNgWord(word, newAction); setNewWord(''); }, `「${word}」を追加しました`);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold">NGワード</h3>
      <p className="text-sm text-gray-600">
        初期値はカード会社提供の「別紙1」です。全角・半角、大文字・小文字、ひらがな・カタカナは区別せず照合します。
      </p>
      <ul className="text-xs text-gray-600">
        {(Object.keys(ACTION_HELP) as NgWordAction[]).map((action) => (
          <li key={action}>・{NG_ACTION_LABELS[action]}: {ACTION_HELP[action]}</li>
        ))}
      </ul>
      {error && <p className="bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</p>}
      {notice && <p className="bg-green-50 p-3 text-sm text-green-800" role="status">{notice}</p>}

      <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-2">
        <label className="text-sm">
          語句
          <input value={newWord} onChange={(event) => setNewWord(event.target.value)} maxLength={50} className="mt-1 block border border-gray-300 px-2 py-1" />
        </label>
        <label className="text-sm">
          扱い
          <select value={newAction} onChange={(event) => setNewAction(event.target.value as NgWordAction)} className="mt-1 block border border-gray-300 px-2 py-1">
            <option value="block">{NG_ACTION_LABELS.block}</option>
            <option value="review">{NG_ACTION_LABELS.review}</option>
          </select>
        </label>
        <button type="submit" className="bg-pink-600 px-4 py-1.5 text-sm font-semibold text-white">追加</button>
        <input
          type="search" value={filter} onChange={(event) => setFilter(event.target.value)}
          placeholder="絞り込み" aria-label="NGワードを絞り込み" className="ml-auto border border-gray-300 px-2 py-1 text-sm"
        />
      </form>

      <table className="w-full text-sm">
        <thead className="bg-gray-100 text-left">
          <tr><th className="p-2">語句</th><th className="p-2">扱い</th><th className="p-2">由来</th><th className="p-2">操作</th></tr>
        </thead>
        <tbody>
          {visible.map((word) => (
            <tr key={word.id} className="border-b">
              <td className="p-2">{word.word}</td>
              <td className="p-2">
                <select
                  aria-label={`${word.word} の扱い`} value={word.action}
                  onChange={(event) => void run(() => apiClient.updateNgWord(word.id, event.target.value as NgWordAction).then(() => undefined), `「${word.word}」の扱いを変更しました`)}
                  className="border border-gray-300 px-1 py-0.5"
                >
                  <option value="block">{NG_ACTION_LABELS.block}</option>
                  <option value="review">{NG_ACTION_LABELS.review}</option>
                </select>
              </td>
              <td className="p-2 text-xs text-gray-500">{word.source === 'sheet1' ? '別紙1' : '追加'}</td>
              <td className="p-2">
                <button type="button" onClick={() => setConfirmDelete(word)} className="border border-red-700 px-2 py-0.5 text-red-700">削除</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-gray-500">{visible.length} / {words.length} 件</p>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-label="NGワードの削除">
          <div className="bg-white p-6 text-left shadow-xl">
            <p>「{confirmDelete.word}」を削除しますか？削除すると照合に使われなくなります。</p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmDelete(null)} className="border border-gray-300 px-3 py-1">キャンセル</button>
              <button
                type="button" className="bg-red-700 px-3 py-1 text-white"
                onClick={() => {
                  const target = confirmDelete;
                  setConfirmDelete(null);
                  void run(() => apiClient.deleteNgWord(target.id), `「${target.word}」を削除しました`);
                }}
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
